import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Secondary tab row: plain text with an underline on the active tab.
 *
 * The app's tab hierarchy is boxed pills for the primary row and this
 * underline row for anything nested under it — see the advisor goal view,
 * where the goal tabs are pills and the page sub-nav (Goal / Portfolio /
 * Analysis / Forecast) is underlined. Lifted out of `goal-context-view` when
 * a second consumer appeared.
 */
export function UnderlineTabs({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <nav className={cn("flex gap-6 border-b flex-wrap", className)}>{children}</nav>;
}

type UnderlineTabProps = {
  label: ReactNode;
  active: boolean;
  hasError?: boolean;
  onClick: () => void;
  icon?: ReactNode;
};

export function UnderlineTab({ label, active, hasError, onClick, icon }: UnderlineTabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "pb-2 text-sm font-medium transition-colors relative inline-flex items-center gap-1.5",
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {label}
      {hasError && <TriangleAlert className="h-3.5 w-3.5 text-destructive" />}
      {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
    </button>
  );
}
